import type { Deck, DeckFormat, DeckStatus } from '@/types/deck';
import { useAtomValue } from 'jotai';
import { ReactElement, useMemo, useState } from 'react';
import useCardGroups, { CardGroup } from '../hooks/useCardGroups';
import { DeckBoard, DeckColumnVisibility, buildGridTemplateColumns, deckEditorSettingsAtom } from '../state/deckEditorSettings';
import { EnrichedDeckCard } from '../types';
import CommanderSection from './CommanderSection';
import DeckCardRow from './DeckListViewRow';
import SectionTitle from './SectionTitle';

export interface DeckListViewProps {
  selectedBoard: DeckBoard;
  deck: Deck;
  cards: EnrichedDeckCard[];
  commanderCards?: EnrichedDeckCard[];
  companionCards?: EnrichedDeckCard[];
  isLocked?: boolean;
  onSetCommander: (cardId: string, scryfallId: string) => void;
  onSetCover: (scryfallId: string) => void;
  /** Called when a card is selected to open details panel */
  onCardSelect?: (scryfallId: string, deckCardId: string) => void;
  /** Whether multi-select mode is active */
  isSelectionMode?: boolean;
  /** Set of selected deck card ids */
  selectedDeckCardIds?: Set<string>;
  /** Toggle selection for a deck card */
  onToggleDeckCard?: (deckCardId: string) => void;
}

export default function DeckListView({
  selectedBoard,
  deck,
  cards,
  commanderCards = [],
  companionCards = [],
  isLocked = false,
  onSetCommander,
  onSetCover,
  onCardSelect,
  isSelectionMode = false,
  selectedDeckCardIds,
  onToggleDeckCard,
}: DeckListViewProps) {
  const cardGroups = useCardGroups(cards);
  const settings = useAtomValue(deckEditorSettingsAtom);

  // Build the CSS Grid template columns based on visibility settings
  const gridTemplateColumns = useMemo(() => buildGridTemplateColumns(settings.columnVisibility), [settings.columnVisibility]);

  return (
    <div className="w-full">
      {/* CSS Grid container for all card sections including commander/companion */}
      <div className="deck-list-grid w-full" style={{ display: 'grid', gridTemplateColumns }}>
        {/* Commander and Companion Section (uses DeckListViewRow for column alignment) */}
        {selectedBoard === 'main' && (commanderCards.length > 0 || companionCards.length > 0) && (
          <CommanderSection
            deckId={deck.id}
            commanders={commanderCards}
            companions={companionCards}
            isLocked={isLocked}
            variant="list"
            deckFormat={deck.format}
            deckStatus={deck.status}
            coverCardId={deck.cover_card_id}
            onSetCover={onSetCover}
            onCardSelect={onCardSelect}
            columnVisibility={settings.columnVisibility}
          />
        )}
        {cardGroups.map((group) => (
          <ListSection
            key={group.key}
            group={group}
            deckId={deck.id}
            deckFormat={deck.format}
            deckStatus={deck.status}
            coverCardId={deck.cover_card_id}
            isLocked={isLocked}
            onSetCover={onSetCover}
            onSetCommander={onSetCommander}
            onCardSelect={onCardSelect}
            columnVisibility={settings.columnVisibility}
            isSelectionMode={isSelectionMode}
            selectedDeckCardIds={selectedDeckCardIds}
            onToggleDeckCard={onToggleDeckCard}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Zone Section Component
// ============================================================================

interface ListSectionProps {
  group: CardGroup<EnrichedDeckCard>;
  deckId: string;
  deckFormat: DeckFormat | null;
  deckStatus: DeckStatus;
  coverCardId: string | null;
  isLocked: boolean;
  onSetCover: (scryfallId: string) => void;
  onSetCommander: (cardId: string, scryfallId: string) => void;
  onCardSelect?: (scryfallId: string, deckCardId: string) => void;
  columnVisibility: DeckColumnVisibility;
  isSelectionMode?: boolean;
  selectedDeckCardIds?: Set<string>;
  onToggleDeckCard?: (deckCardId: string) => void;
}

function ListSection({
  group,
  deckId,
  deckFormat,
  deckStatus,
  coverCardId,
  isLocked,
  onSetCover,
  onSetCommander,
  onCardSelect,
  columnVisibility,
  isSelectionMode = false,
  selectedDeckCardIds,
  onToggleDeckCard,
}: ListSectionProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(true);

  if (group.cards.length === 0) {
    return <></>;
  }

  return (
    <>
      {/* Section header spans all columns */}
      <SectionTitle
        title={group.label}
        uniqueCount={group.cards.length}
        totalCount={group.totalQuantity}
        onClick={() => setIsExpanded(!isExpanded)}
        isExpanded={isExpanded}
        columnVisibility={columnVisibility}
      />

      {/* Card rows use display: contents to participate in parent grid */}
      {isExpanded &&
        group.cards.map((deckCard) => (
          <DeckCardRow
            key={deckCard.id}
            deckCard={deckCard}
            deckId={deckId}
            deckFormat={deckFormat}
            deckStatus={deckStatus}
            isCover={deckCard.scryfall_id === coverCardId}
            isLocked={isLocked}
            onSetCover={onSetCover}
            onSetCommander={onSetCommander}
            onCardSelect={onCardSelect}
            isSelectionMode={isSelectionMode}
            isSelected={selectedDeckCardIds?.has(deckCard.id)}
            onToggleSelect={onToggleDeckCard}
          />
        ))}
    </>
  );
}
